/**
 * @file DetailsForm.js
 * @description This file contains the DetailsForm component, which is a reusable form for displaying and editing an agencies Details information.
 */

import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from 'react';
import { getUsStatesTerritories } from '../../utils/utils';
import { Dropdown } from '../helpers/Dropdown';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import style from './AgencyPage.module.scss';
import Toast from '../ui/toasts/Toast';
import {
    createAgency,
    updateAgency,
    fetchClientTiers,
} from '../../services/systemService';
import { formatDateToMMDDYYYY } from '../helpers/DateUtils';
import { useAuthContext } from '../../store/AuthContext';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const DetailsForm = forwardRef(({ onSave, initialData = {} }, ref) => {
    const [detailsData, setDetailsData] = useState({ ...initialData });

    const [isLoading, setIsLoading] = useState(false);
    const [usStatesTerritories, setUsStatesTerritories] = useState([]);
    const [clientTiers, setClientTiers] = useState([]);
    const [errors, setErrors] = useState({});
    const isCreateMode = initialData?.id == -1;

    const { hasAttribute } = useAuthContext();
    const canEditForm = hasAttribute(SecurityAttributes.AgencyFullAccess);

    const formFields = [
        'id', // required only when isCreateMode is false
        'name', // required
        'code', // required
        'address',
        'address2',
        'city',
        'stateAbbreviation',
        'zip',
        'vanityURL',
        'isReseller',
        'tenantTiers',
    ];

    useEffect(() => {
        const getClientTiers = async () => {
            setIsLoading(true);
            try {
                const data = await fetchClientTiers();
                setClientTiers(data);
            } catch (error) {
                console.error('Could not fetch client tiers:', error);
                Toast.error(getText('msg_436b8e9da603'));
            } finally {
                setIsLoading(false);
            }
        };

        getClientTiers();

        const states = getUsStatesTerritories().map(e => ({
            label: e.name,
            value: e.acronym,
        }));
        setUsStatesTerritories(states);
    }, []);

    const handleInputChange = event => {
        const { name, value } = event.target;
        setDetailsData(prevData => ({
            ...prevData,
            [name]: value === '' ? null : value,
        }));
    };

    const validateField = (name, value = '') => {
        const isEmpty = `${value}`.trim() === '' || !value;
        const requiredFields = ['name', 'code'];

        if (requiredFields.includes(name) && isEmpty) {
            return `${convertCamelCaseToText(name)} is required.`;
        }

        return '';
    };

    /**
     * Converts a camelCase string to regular text.
     * @param {string} str - The camelCase string to convert.
     * @returns {string} - The converted regular text.
     */
    const convertCamelCaseToText = str => {
        const result = str.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    const resolveSaveInvocation = argument => {
        if (argument && typeof argument.preventDefault === 'function') {
            return { event: argument, options: {} };
        }

        return { event: null, options: argument || {} };
    };

    const handleSave = async invocation => {
        if (isLoading) return { success: false };

        const { event, options } = resolveSaveInvocation(invocation);
        event?.preventDefault();

        const trimmedData = Object.keys(detailsData).reduce((acc, key) => {
            const value = detailsData[key];
            acc[key] = typeof value === 'string' ? value.trim() : value;
            return acc;
        }, {});

        const newErrors = {};
        setErrors(newErrors);

        formFields.forEach(field => {
            if (field === 'id' && isCreateMode) return;
            const error = validateField(field, trimmedData[field]);
            if (error) {
                newErrors[field] = error;
            }
        });

        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);

            const errorList = Object.keys(newErrors);
            if (errorList?.length) {
                Toast.error(newErrors[errorList[0]]);
            } else {
                Toast.error(getText('msg_eacdbb2e3c97'));
            }
            return { success: false, errors: newErrors };
        }

        if (isCreateMode) {
            return createAgencyRecord(trimmedData, options);
        }

        return updateAgencyRecord(trimmedData, options);
    };

    useImperativeHandle(ref, () => ({
        handleSave,
    }));

    const createAgencyRecord = async (detailsData, options = {}) => {
        try {
            const response = await createAgency(detailsData);
            Toast.success(getText('msg_ed60a26a1ff1'));

            if (!options.suppressOnSaveCallback) {
                onSave?.(response);
            }

            return { success: true, agencyRecord: response };
        } catch (error) {
            console.error('Error creating agency:', error);
            if (error?.response?.data?.detail) {
                Toast.error(error.response.data.detail);
            } else {
                Toast.error(getText('msg_d0bcf3807245'));
            }

            return { success: false, error };
        }
    };

    const updateAgencyRecord = async (updatedData, options = {}) => {
        try {
            setIsLoading(true);
            await updateAgency(updatedData);
            Toast.success(getText('msg_edd1b1be525e'));

            if (!options.suppressOnSaveCallback) {
                onSave?.();
            }

            return { success: true };
        } catch (error) {
            console.error('Unable to save changes', error);
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else Toast.error(getText('msg_3c2ce6f3c1a9'));

            return { success: false, error };
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form className={style.detailsFormContainer}>
            <div className={`row p-5 pb-1 pb-4 pt-4 ${style.customListSecond}`}>
                <div className="col-12 col-xs-12 col-md-12 col-lg-8">
                    <div className="form-group mb-3">
                        <div htmlFor="code" className="fw-medium">
                            {getText('msg_30ff37c81664')} <span className="text-danger">*</span>
                        </div>
                        <input
                            id="code"
                            type="text"
                            maxLength="20"
                            className={`form-control ${errors.code ? 'is-invalid' : ''
                                }`}
                            value={detailsData.code || ''}
                            onChange={handleInputChange}
                            name="code"
                            placeholder={getText('msg_74aa6f3f5a9f')}
                            required
                        />
                        <small className="text-muted">
                            {`Character Limit ${detailsData.code?.length || 0}/20`}
                        </small>
                    </div>

                    <div className="form-group mb-3">
                        <div htmlFor="name" className="fw-medium">
                            {getText('msg_515990ab0996')} <span className="text-danger">*</span>
                        </div>
                        <input
                            id="name"
                            type="text"
                            maxLength="20"
                            className={`form-control ${errors.name ? 'is-invalid' : ''
                                }`}
                            value={detailsData.name || ''}
                            onChange={handleInputChange}
                            name="name"
                            placeholder={getText('msg_e9e4d3cdbc4e')}
                            required
                        />
                        <small className="text-muted">
                            {`Character Limit ${detailsData.name?.length || 0}/20`}
                        </small>
                    </div>

                    <div className="form-group mb-3">
                        <label htmlFor="address" className="fw-medium">
                            <i className="bi bi-envelope me-2" />
                            {getText('msg_d70f93df5e8f')}
                        </label>
                        <div className="d-flex gap-3">
                            <input
                                id="address"
                                type="text"
                                className={`form-control ${errors.address ? 'is-invalid' : ''
                                    }`}
                                value={detailsData.address || ''}
                                onChange={handleInputChange}
                                name="address"
                                placeholder={getText('msg_d06d7b99dd4c')}
                            />
                            <input
                                id="address2"
                                type="text"
                                className={`form-control ${errors.address2 ? 'is-invalid' : ''
                                    }`}
                                value={detailsData.address2 || ''}
                                onChange={handleInputChange}
                                name="address2"
                                placeholder={getText('msg_90c1c191e198')}
                            />
                        </div>
                    </div>

                    <div className="d-flex gap-3 mb-3">
                        <div className="form-group" style={{ flex: 2 }}>
                            <label htmlFor="city" className="fw-medium">
                                {getText('msg_4271627f4f0b')}
                            </label>
                            <input
                                id="city"
                                type="text"
                                className={`form-control ${errors.city ? 'is-invalid' : ''
                                    }`}
                                value={detailsData.city || ''}
                                onChange={handleInputChange}
                                name="city"
                                placeholder={getText('msg_8b156d5a51b1')}
                            />
                        </div>
                        <div className="form-group" style={{ flex: 2 }}>
                            <label
                                htmlFor="stateAbbreviation"
                                className="fw-medium"
                            >
                                {getText('msg_a72502067518')}
                            </label>
                            <Dropdown
                                name="stateAbbreviation"
                                height="48px"
                                defaultValue={() => {
                                    const matchingState =
                                        usStatesTerritories.find(
                                            s =>
                                                s.value ===
                                                detailsData.stateAbbreviation,
                                        );
                                    return matchingState
                                        ? matchingState.label
                                        : 'Select';
                                }}
                                options={usStatesTerritories}
                                onChange={stateAbbreviation => {
                                    setDetailsData(prev => ({
                                        ...prev,
                                        stateAbbreviation,
                                    }));
                                }}
                                value={detailsData.stateAbbreviation || ''}
                                disabled={usStatesTerritories.length === 0}
                            />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="zip" className="fw-medium">
                                {getText('msg_d9d220ab2925')}
                            </label>
                            <input
                                id="zip"
                                type="text"
                                maxLength="20"
                                className={`form-control ${errors.zip ? 'is-invalid' : ''
                                    }`}
                                value={detailsData.zip || ''}
                                onChange={handleInputChange}
                                name="zip"
                                placeholder={getText('msg_c79a6f009b44')}
                            />
                        </div>
                    </div>

                    <div className="form-group mb-4">
                        <label htmlFor="vanityURL" className="fw-medium">
                            {getText('msg_74d8cfc8af33')}
                        </label>
                        <input
                            id="vanityURL"
                            type="text"
                            maxLength="20"
                            className={`form-control ${errors.vanityURL ? 'is-invalid' : ''
                                }`}
                            value={detailsData.vanityURL || ''}
                            onChange={handleInputChange}
                            name="vanityURL"
                            placeholder={getText('msg_85539a2546c2')}
                        />
                    </div>

                    <div className="form-group mb-3">
                        <div
                            className="d-flex align-items-center mb-2"
                            style={{ gap: '10px' }}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={detailsData.isReseller}
                                onChange={e => {
                                    setDetailsData(prev => ({
                                        ...prev,
                                        isReseller: e.target.checked,
                                        tenantTiers: [],
                                    }));
                                }}
                                id="isReseller"
                                name="isReseller"
                                style={{ width: '20px', height: '20px' }}
                            />
                            <label
                                htmlFor="isReseller"
                                className="fw-medium mb-0"
                                style={{ fontSize: '16px' }}
                            >
                                {getText('msg_a259ac911046')}{' '}
                                {detailsData.isReseller && (
                                    <span className="text-danger">*</span>
                                )}
                            </label>
                        </div>
                        <SearchableDropdown
                            placeholderLabel={getText('msg_7a177ef1cca0')}
                            options={clientTiers}
                            optionIDField="id"
                            savedOptions={clientTiers
                                .map(tier =>
                                    detailsData.tenantTiers?.includes(tier.id)
                                        ? tier
                                        : null,
                                )
                                .filter(Boolean)}
                            savedOptionIDField="id"
                            updateOptions={tenantTiers => {
                                setDetailsData(prev => ({
                                    ...prev,
                                    tenantTiers: tenantTiers.map(t => t.id),
                                }));
                            }}
                            disabled={!detailsData.isReseller}
                        />
                    </div>

                    <div className="d-flex justify-content-between">
                        <p>
                            <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                        </p>
                        {canEditForm && (
                            <span
                                onClick={handleSave}
                                type="button"
                                className="ms-3 button-base ing-button-primary-left align-middle"
                            >
                                <i className="bi bi-check-lg me-2" />
                                {getText('msg_efc007a393f6')}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </form>
    );
});

export default DetailsForm;
